import React from 'react';
import { NavLink } from 'react-router-dom';
import { Globe, MapPin, Mail, Phone, ChevronRight } from 'lucide-react';
import { useCMS } from '../context/CMSContext';

const Footer = () => {
  const { settings } = useCMS();
  const s = (key, fallback) => settings[key] || fallback;

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Initiatives', path: '/initiatives' },
    { name: 'Impact', path: '/impact' },
    { name: 'Partnership', path: '/partnership' },
    { name: 'Team', path: '/team' },
    { name: 'News', path: '/news' },
  ];

  return (
    <footer className="bg-brandBlack text-brandWhite pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12 lg:gap-16 mb-16">
          
          {/* Column 1: Brand Info */}
          <div>
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-brandGreen rounded-full flex items-center justify-center shrink-0">
                <Globe className="text-brandWhite w-6 h-6" />
              </div>
              <span 
                className="text-2xl font-bold text-brandWhite" 
                dangerouslySetInnerHTML={{__html: s('site_title_plain', 'Eunikare International')}}
              ></span>
            </div>
            <p className="text-brandWhite/70 text-sm leading-relaxed mb-8 max-w-sm">
              {s('footer_about', 'A registered nonprofit organization in Uganda advancing education, health, entrepreneurship, and social innovation to build resilient and self-sustaining communities.')}
            </p>
            <div className="flex space-x-4">
              <a href="https://x.com/Eunikare" target="_blank" rel="noreferrer" aria-label="Twitter" className="w-10 h-10 rounded-full border border-brandWhite/20 flex items-center justify-center hover:bg-brandGreen hover:border-brandGreen transition-colors">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-brandWhite" fill="currentColor" aria-hidden="true">
                  <path d="M22.46 6c-.77.35-1.57.58-2.42.69a4.2 4.2 0 0 0 1.84-2.32 8.35 8.35 0 0 1-2.66 1.02 4.16 4.16 0 0 0-7.1 3.8A11.8 11.8 0 0 1 3.1 4.8 4.13 4.13 0 0 0 4.1 10a4.16 4.16 0 0 1-1.88-.52v.05a4.16 4.16 0 0 0 3.34 4.08 4.2 4.2 0 0 1-1.87.07 4.16 4.16 0 0 0 3.88 2.9A8.36 8.36 0 0 1 2 18.58a11.75 11.75 0 0 0 6.29 1.84c7.55 0 11.68-6.26 11.68-11.68 0-.18-.01-.36-.02-.54A8.33 8.33 0 0 0 24 5.56a8.2 8.2 0 0 1-2.36.65 4.1 4.1 0 0 0 1.8-2.27" />
                </svg>
              </a>
              <a href="https://www.linkedin.com/company/eunikare-international/" target="_blank" rel="noreferrer" aria-label="LinkedIn" className="w-10 h-10 rounded-full border border-brandWhite/20 flex items-center justify-center hover:bg-brandGreen hover:border-brandGreen transition-colors">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-brandWhite" fill="currentColor" aria-hidden="true">
                  <path d="M4.98 3.5C4.98 5.43 3.4 7 1.48 7S0 5.43 0 3.5 1.58 0 3.5 0 4.98 1.57 4.98 3.5zM.28 24h4.4V7.74H.28V24zM7.97 7.74h4.22v2.2h.06c.59-1.1 2.03-2.26 4.18-2.26 4.48 0 5.3 2.95 5.3 6.78V24h-4.4v-7.53c0-1.8-.03-4.12-2.51-4.12-2.51 0-2.9 1.96-2.9 4V24H7.97V7.74z" />
                </svg>
              </a>
              <a href="https://www.facebook.com/share/17mrfL2GY1/" target="_blank" rel="noreferrer" aria-label="Facebook" className="w-10 h-10 rounded-full border border-brandWhite/20 flex items-center justify-center hover:bg-brandGreen hover:border-brandGreen transition-colors">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-brandWhite" fill="currentColor" aria-hidden="true">
                  <path d="M22.675 0H1.325C.593 0 0 .593 0 1.326v21.348C0 23.408.593 24 1.325 24h11.495v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.658-4.788 1.325 0 2.464.099 2.796.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116C23.406 24 24 23.408 24 22.674V1.326C24 .593 23.406 0 22.675 0z" />
                </svg>
              </a>
              <a href="https://www.instagram.com/eunikare?igsh=cG90bW9objB5bnQ0" target="_blank" rel="noreferrer" aria-label="Instagram" className="w-10 h-10 rounded-full border border-brandWhite/20 flex items-center justify-center hover:bg-brandGreen hover:border-brandGreen transition-colors">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-brandWhite" fill="currentColor" aria-hidden="true">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.17.054 1.97.24 2.427.412a4.92 4.92 0 0 1 1.78 1.04 4.92 4.92 0 0 1 1.04 1.78c.172.457.358 1.257.412 2.427.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.054 1.17-.24 1.97-.412 2.427a4.92 4.92 0 0 1-1.04 1.78 4.92 4.92 0 0 1-1.78 1.04c-.457.172-1.257.358-2.427.412-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.17-.054-1.97-.24-2.427-.412a4.92 4.92 0 0 1-1.78-1.04 4.92 4.92 0 0 1-1.04-1.78c-.172-.457-.358-1.257-.412-2.427C2.175 15.747 2.163 15.367 2.163 12s.012-3.584.07-4.85c.054-1.17.24-1.97.412-2.427a4.92 4.92 0 0 1 1.04-1.78 4.92 4.92 0 0 1 1.78-1.04c.457-.172 1.257-.358 2.427-.412C8.416 2.175 8.796 2.163 12 2.163zm0-2.163C8.741 0 8.332.013 7.052.072 5.777.131 4.751.315 3.902.662 3.005 1.029 2.24 1.563 1.56 2.244.88 2.924.346 3.69-.021 4.587c-.347.849-.531 1.875-.59 3.15C-.011 8.332 0 8.741 0 12s-.013 3.668.072 4.948c.059 1.275.243 2.301.59 3.15.367.897.901 1.663 1.581 2.343.68.68 1.446 1.214 2.343 1.581.849.347 1.875.531 3.15.59C8.332 23.989 8.741 24 12 24s3.668-.013 4.948-.072c1.275-.059 2.301-.243 3.15-.59.897-.367 1.663-.901 2.343-1.581.68-.68 1.214-1.446 1.581-2.343.347-.849.531-1.875.59-3.15.059-1.28.072-1.689.072-4.948s-.013-3.668-.072-4.948c-.059-1.275-.243-2.301-.59-3.15-.367-.897-.901-1.663-1.581-2.343-.68-.68-1.446-1.214-2.343-1.581-.849-.347-1.875-.531-3.15-.59C15.668.013 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zm0 10.162a3.999 3.999 0 1 1 0-7.998 3.999 3.999 0 0 1 0 7.998zm6.406-11.845a1.44 1.44 0 1 0 0 2.88 1.44 1.44 0 0 0 0-2.88z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Column 2: Contact Us */}
          <div>
            <div className="mb-6">
              <h4 className="text-brandMauve font-bold text-lg">Contact Us</h4>
              <div className="w-12 h-[2px] bg-brandGreen mt-2"></div>
            </div>
            <div className="space-y-6 text-brandWhite/70">
              
              {/* Address */}
              <div className="flex items-start space-x-4 border-b border-brandWhite/10 pb-6">
                <div className="w-10 h-10 rounded-full border border-brandGreen flex items-center justify-center text-brandGreen shrink-0 mt-1">
                  <MapPin className="w-5 h-5" />
                </div>
                <div className="text-sm space-y-1">
                  <h5 className="font-bold text-brandWhite text-base mb-1">Address</h5>
                  <p>P.O.BOX 123139</p>
                  <p>Plot 40 Jude Ogik Avenue, Kitgum Municipality</p>
                  <p>Kampala, Uganda</p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-center space-x-4 border-b border-brandWhite/10 pb-6">
                <div className="w-10 h-10 rounded-full border border-brandGreen flex items-center justify-center text-brandGreen shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <a href="mailto:info@eunikareinternational.org" className="hover:text-brandGreen text-sm transition-colors">
                  info@eunikareinternational.org
                </a>
              </div>

              {/* Website */}
              <div className="flex items-center space-x-4 border-b border-brandWhite/10 pb-6">
                <div className="w-10 h-10 rounded-full border border-brandGreen flex items-center justify-center text-brandGreen shrink-0">
                  <Globe className="w-5 h-5" />
                </div>
                <a href="https://www.eunikareinternational.org" target="_blank" rel="noreferrer" className="hover:text-brandGreen text-sm transition-colors">
                  https://www.eunikareinternational.org
                </a>
              </div>

              {/* Call on */}
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 rounded-full border border-brandGreen flex items-center justify-center text-brandGreen shrink-0 mt-1">
                  <Phone className="w-5 h-5" />
                </div>
                <div className="text-sm space-y-1">
                  <p>Office Line: +256 200 91 77 55</p>
                  <p>Registration Number: 80034135864265</p>
                  <p>
                    or WhatsApp us on:{' '}
                    <a
                      href="https://wa.me/256787365512"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-brandGreen underline transition-colors"
                    >
                      +256 787 365 512
                    </a>
                  </p>
                </div>
              </div>

            </div>
          </div>

          {/* Column 3: Quick Links */}
          <div>
            <div className="mb-6">
              <h4 className="text-brandGreen font-bold text-lg">Quick Links</h4>
              <div className="w-12 h-[2px] bg-brandGreen mt-2"></div>
            </div>
            <ul className="text-brandWhite/70">
              {quickLinks.map((link) => (
                <li key={link.path} className="border-b border-brandWhite/10 py-3.5 flex items-center">
                  <ChevronRight className="w-4 h-4 text-brandGreen mr-3 shrink-0" />
                  <NavLink className="hover:text-brandGreen transition-colors text-sm font-medium" to={link.path}>
                    {link.name}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

        </div>

        {/* Bottom Banner */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-center text-brandWhite/60 text-sm gap-4 sm:gap-0">
          <div className="hidden sm:block flex-grow h-[1px] bg-brandGreen/25 mr-6"></div>
          <p className="shrink-0 text-center px-4">
            © {new Date().getFullYear()} {s('site_title_plain', 'Eunikare International')}. All rights reserved.
          </p>
          <div className="hidden sm:block flex-grow h-[1px] bg-brandGreen/25 ml-6"></div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;

